import { Avatar } from "@/components/buttons/Avatar";
import { useColorMode } from "@/components/layouts/ColorModeContext";
import { dealiasArchetype } from "@/features/branding";
import { decryptData } from "@/features/cryptography";
import { store, StoreState } from "@/store";
import { FacadesBatchPostResponse } from "@/store/services/facades/types";
import { EncryptedAnalysisWithPointId, supabaseApi, useGetCurrentUserQuery, useLazyGetAdaptationsQuery, useLazyGetUnprocessedAdaptationPathwayActionsQuery, useLazyGetUnprocessedPortfolioAssetCountQuery, useLazyGetUnprocessedPortfolioAssetIdsQuery, useUpdateAdaptationsStampsMutation, useUpdatePortfolioStampsMutation } from "@/store/services/supabase";
import { dispatchWithRetries } from "@/store/services/supabase/utils";
import { portfolioExportActions } from "@/store/slices/portfolioExport";
import { PortfolioExportJobActions } from "@/tools/aggregate/portfolio-export/components/PortfolioExportJobActions";
import { PortfolioExportJobErrorModal } from "@/tools/aggregate/portfolio-export/components/PortfolioExportJobErrorModal";
import { PortfolioExportJobStatus } from "@/tools/aggregate/portfolio-export/components/PortfolioExportJobStatus";
import { PortfolioExportProgressMessages } from "@/tools/aggregate/portfolio-export/components/PortfolioExportProgressMessages";
import { PortfolioExportJobSchema, portfolioTagStyle } from "@/tools/aggregate/portfolio-export/types";
import { getPortfolioExportPostAction } from "@/tools/aggregate/portfolio-export/utils/post";
import { getAdaptationsDone, getAnalysisComplete, getAssetsDone, getHasAdaptationPhase, getIsAdaptationProcessing } from "@/tools/aggregate/portfolio-export/utils/progress";
import { maxLssPoints } from "@/tools/create/lss-from-map/constants";
import { autoAdaptationTypeValues } from "@/types/adaptation";
import { hexToArrayBuffer } from "@/utils/crypto";
import { formatRelativeDate, getDaysAgo } from "@/utils/datetime";
import { useKeys } from "@/utils/hooks/useKeys";
import { is5xxHttpStatus } from "@/utils/http";
import { withCommas } from "@/utils/number";
import { LockFilled } from "@ant-design/icons";
import { ChecksIcon } from "@phosphor-icons/react";
import * as Sentry from "@sentry/nextjs";
import { Button, Col, Row, Tag, Tooltip } from "antd";
import classnames from "classnames";
import _ from "lodash";
import { cloneDeep } from "lodash/fp";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import styles from "./PortfolioExportJobInformation.module.scss";
const SUPABASE_BATCH_SIZE = 100;
export type AnalysisItemType = "point" | "adaptation";
export type AnalysisInputProps = {
  id: string;
  analysisType: AnalysisItemType;
  supabaseId: string;
  data: string;
};
interface PortfolioExportJobInformationProps {
  onDelete: () => void;
  onEdit: (props: {
    schema: PortfolioExportJobSchema;
    name: string;
    isPrivate: boolean;
    groupId: string;
  }) => Promise<void>;
  schema: PortfolioExportJobSchema;
  updateSchema: (schema: PortfolioExportJobSchema) => void;
  onError: (err: string | string[], portfolioId: string) => void;
  canRunAutoAdaptation: boolean;
  availablePortfolioTypes?: string[];
  isTutorial?: boolean;
}
export const PortfolioExportJobInformation = ({
  onDelete,
  onEdit,
  schema,
  updateSchema,
  onError,
  canRunAutoAdaptation,
  availablePortfolioTypes = [],
  isTutorial = false
}: PortfolioExportJobInformationProps) => {
  const {
    colorMode
  } = useColorMode();
  const [kp] = useKeys();
  const [getUnprocessedPortfolioAssets] = useLazyGetUnprocessedPortfolioAssetIdsQuery();
  const [getUnprocessedPortfolioAssetCount] = useLazyGetUnprocessedPortfolioAssetCountQuery();
  const [getUnprocessedAdaptationPathwayActions] = useLazyGetUnprocessedAdaptationPathwayActionsQuery();
  const [updatePortfolioStamps] = useUpdatePortfolioStampsMutation();
  const [updateAdaptationsStamps] = useUpdateAdaptationsStampsMutation();
  const [getAdaptations] = useLazyGetAdaptationsQuery();
  const {
    data: currentUser
  } = useGetCurrentUserQuery();
  const [showError, setShowError] = useState(false);
  const isAdaptationProcessingRef = useRef(false);
  const autoAdaptationStatusRef = useRef(schema.autoAdaptationStatus);
  autoAdaptationStatusRef.current = schema.autoAdaptationStatus;
  useEffect(() => {
    refreshPortfolioErrorCount();
    if (schema.status !== "uploading" || !kp.user) return;
    getAssets();
  }, [kp.user]);
  useEffect(() => {
    if (!canRunAutoAdaptation || !kp.user) return;

    // no actual pending adaptations during tutorial
    if (isTutorial) return;

    // while schema is processing
    if (!["completed", "completed_with_errors"].includes(schema.status)) return;

    // if autoadaptation is complete/missing
    if (schema.autoAdaptationStatus !== "pending") return;

    // prevent multiple rerun requests for same schema
    if (isAdaptationProcessingRef.current) return;
    const runPendingAdaptationActions = async () => {
      isAdaptationProcessingRef.current = true;
      try {
        await processPendingAdaptationActions();
      } catch (err) {
        console.error("Error processing pending adaptation actions", err);
      } finally {
        isAdaptationProcessingRef.current = false;
      }
    };
    runPendingAdaptationActions();
  }, [schema.id, schema.status, schema.autoAdaptationStatus, currentUser?.id, canRunAutoAdaptation, kp.user, isTutorial]);
  const refreshPortfolioErrorCount = async () => {
    if (!schema.assets.errorCount) {
      return;
    }
    const action = supabaseApi.endpoints.getPortfolioAssetErrorsCount.initiate(schema.id);
    const {
      data: errorCount
    } = await store.dispatch(action);
    if (errorCount !== undefined && errorCount !== schema.assets.errorCount) {
      await store.dispatch(supabaseApi.endpoints.updatePortfolioProgressCounts.initiate({
        id: schema.id,
        total: schema.assets.totalCount,
        pending: schema.assets.unprocessedCount,
        error: errorCount,
        success: schema.assets.totalCount - errorCount
      }));
      updateSchema({
        ...cloneDeep(schema),
        status: errorCount ? "completed_with_errors" : "completed",
        assets: {
          ...schema.assets,
          errorCount,
          processedCount: schema.assets.totalCount - errorCount
        }
      });
    }
  };
  const markAdaptationActionsAsProcessed = async (batch: {
    id: string;
  }[]) => {
    const action = supabaseApi.endpoints.setAdaptationPathwayActionIsProcessedToTrue.initiate(batch);
    return await dispatchWithRetries<null>({
      action,
      maxRetries: 5,
      timeout: 5000
    });
  };
  const updatePortfolioAutoAdaptationStamps = async ({
    portfolioId,
    stamps
  }: {
    portfolioId: string;
    stamps: string[];
  }) => {
    const {
      data: adaptations = [],
      error
    } = await getAdaptations({
      portfolioId,
      adaptationTypes: [...autoAdaptationTypeValues]
    });
    if (error || !adaptations?.length) {
      console.log("could not find adaptations in portfolio to update stamps");
    }
    return await updateAdaptationsStamps({
      ids: adaptations.map(({
        id
      }: {
        id: string;
      }) => id),
      stamps: stamps
    });
  };
  const decryptedAnalysisInputWithType = async (data: EncryptedAnalysisWithPointId[], analysisType: AnalysisItemType = "point"): Promise<AnalysisInputProps[]> => Promise.all(data.map(async d => {
    // Todo: Transform to the shape for batch instead of removing an extra property
    let _parsed = JSON.parse(await decryptData(schema.secret, hexToArrayBuffer(d.data || "")));
    _parsed.archetype = dealiasArchetype<string>(_parsed.archetype);

    // Exclude geocoding.address when both LatLongs and Address supplied
    if (_parsed?.coordinates?.latitude && _parsed?.geocoding?.address) {
      _parsed = _.omit(_parsed, "geocoding");
    }
    return {
      id: await decryptData(schema.secret, hexToArrayBuffer(d.id)),
      analysisType,
      supabaseId: d.supabaseId,
      data: JSON.stringify(_.omit(_parsed, "userSuppliedId"))
    };
  }));
  const fetchAdaptationPathwayActions = async (portfolioAssetIds: string[]) => {
    if (portfolioAssetIds.length === 0 || !canRunAutoAdaptation) {
      return [];
    }
    const {
      data: unprocessedActions = [] as EncryptedAnalysisWithPointId[],
      error: unprocessedActionsError
    } = await getUnprocessedAdaptationPathwayActions({
      portfolioId: schema.id,
      portfolioAssetIds
    });
    if (unprocessedActionsError || !unprocessedActions.length) {
      console.log("[PORTFOLIO] adaptation actions not found");
      return [];
    }
    return unprocessedActions;
  };
  const getAssets = async () => {
    const data: EncryptedAnalysisWithPointId[] = await getUnprocessedPortfolioAssets({
      portfolioId: schema.id,
      length: SUPABASE_BATCH_SIZE
    }).unwrap();
    const {
      data: unprocessedCount
    } = await getUnprocessedPortfolioAssetCount({
      portfolioId: schema.id
    });
    if (data.length !== 0) {
      if (unprocessedCount) {
        store.dispatch(portfolioExportActions.setNewRunTotalInBatchTo({
          id: schema.id,
          count: unprocessedCount
        }));
      }
      updateSchema({
        ...cloneDeep(schema),
        status: "uploading"
      });
      const supabaseIds: {
        id: string;
      }[] = data.map((d: EncryptedAnalysisWithPointId) => ({
        id: d.supabaseId
      }));

      // decrypting portfolio asset data
      const decryptedAnalysisInput = await decryptedAnalysisInputWithType(data, "point");

      // list to send to batch. TODO: hilbert sort
      const unsortedAnalysisInputList = [...decryptedAnalysisInput];
      if (canRunAutoAdaptation) {
        const unprocessedActions = await fetchAdaptationPathwayActions(data.map(d => d.supabaseId));
        const decryptedActionAnalysisInput = await decryptedAnalysisInputWithType(unprocessedActions, "adaptation");
        unsortedAnalysisInputList.push(...decryptedActionAnalysisInput);
      }
      const decryptedAnalysisInputList = unsortedAnalysisInputList.map(({
        data
      }) => data);
      await sendBatch(decryptedAnalysisInputList).then(async data => {
        await markAsProcessed(supabaseIds);
        if (canRunAutoAdaptation) {
          const processedActionSupabaseIds = unsortedAnalysisInputList.filter(({
            analysisType
          }) => analysisType === "adaptation").map(({
            supabaseId
          }) => ({
            id: supabaseId
          }));
          await markAdaptationActionsAsProcessed(processedActionSupabaseIds);
        }
        if (data.stamps && data.stamps.length) {
          await updatePortfolioStamps({
            id: schema.id,
            stamps: data.stamps
          });
          if (canRunAutoAdaptation) {
            await updatePortfolioAutoAdaptationStamps({
              portfolioId: schema.id,
              stamps: data.stamps
            });
          }
        }
        updateSchema({
          ...cloneDeep(schema),
          stamps: data.stamps || []
        });
        return data;
      }).catch(async err => onError(err, schema.id));
      await getAssets();
    } else {
      updateSchema({
        ...cloneDeep(schema),
        status: "processing"
      });
    }
  };
  const sendBatch = async (batch: string[]) => {
    console.log("[PORTFOLIO] sendBatch");
    const action = getPortfolioExportPostAction({
      batch,
      priority: schema.assets.totalCount <= maxLssPoints ? "prioritized" : undefined
    }, schema.regionType);
    const data = await dispatchWithRetries<FacadesBatchPostResponse>({
      action,
      maxRetries: 5,
      shouldRetryFn: (value: FacadesBatchPostResponse) => {
        if (value.error_summary && value.error_summary.length && value.error_summary.some(({
          status
        }) => is5xxHttpStatus(status))) {
          return {
            retry: true,
            message: "Server has had an error. Will retry."
          };
        }
        return {
          retry: false
        };
      },
      timeout: 5000
    });
    if (data.errors > 0) {
      if (data.error_summary) {
        throw data.error_summary.map(({
          title,
          detail,
          status
        }) => {
          if (status === 422) {
            Sentry.captureException(`Analysis Error: sendBatch: ${status}`, {
              tags: {
                section: "Analysis Error"
              },
              extra: {
                userId: currentUser?.id,
                orgId: currentUser?.org_id,
                errorSummary: data.error_summary,
                title,
                description: detail
              }
            });
            return "The uploaded file does not match the expected format.";
          }
          if (is5xxHttpStatus(status)) {
            return "An unexpected incident has occured on the server.";
          }
          return `${title}: ${detail}`;
        });
      }
      throw ["Errors occured when uploading data - please try again"];
    }
    return data;
  };
  const markAsProcessed = async (batch: {
    id: string;
  }[]) => {
    console.log("[PORTFOLIO] markAsProcessed ");
    const action = supabaseApi.endpoints.setPortfolioAssetIsProcessedToTrue.initiate(batch);
    return await dispatchWithRetries<null>({
      action,
      maxRetries: 5,
      timeout: 5000
    });
  };
  const processPendingAdaptationActions = async () => {
    // baseline marked processed, so this will return other auto adaptation actions
    const unprocessedActions = await getUnprocessedAdaptationPathwayActions({
      portfolioId: schema.id,
      portfolioAssetIds: undefined
    }).unwrap();
    if (!unprocessedActions.length) return;
    const decryptedActions = await decryptedAnalysisInputWithType(unprocessedActions, "adaptation");
    for (let i = 0; i < decryptedActions.length; i += SUPABASE_BATCH_SIZE) {
      if (autoAdaptationStatusRef.current !== "pending") return;
      const chunk = decryptedActions.slice(i, i + SUPABASE_BATCH_SIZE);
      await sendBatch(chunk.map(({
        data
      }) => data)).then(async data => {
        await markAdaptationActionsAsProcessed(chunk.map(({
          supabaseId
        }) => ({
          id: supabaseId
        })));
        if (data.stamps && data.stamps.length) {
          await updatePortfolioStamps({
            id: schema.id,
            stamps: data.stamps
          });
          await updatePortfolioAutoAdaptationStamps({
            portfolioId: schema.id,
            stamps: data.stamps
          });
        }
      }).catch(async err => onError(err, schema.id));
    }
  };
  const isAdaptationProcessing = getIsAdaptationProcessing(schema, canRunAutoAdaptation);
  return <Col span={24} data-sentry-element="Col" data-sentry-component="PortfolioExportJobInformation" data-sentry-source-file="PortfolioExportJobInformation.tsx">
      <Row className="items-center justify-between gap-y-2" data-sentry-element="Row" data-sentry-source-file="PortfolioExportJobInformation.tsx">
        <Col data-sentry-element="Col" data-sentry-source-file="PortfolioExportJobInformation.tsx">
          <div className="flex items-center gap-2 font-medium text-gray-900">
            <div className="flex h-4 w-4 items-center justify-center overflow-hidden">
              <PortfolioExportJobStatus schema={schema} iconOnly canRunAutoAdaptation={canRunAutoAdaptation} isAdaptationProcessing={isAdaptationProcessing} data-sentry-element="PortfolioExportJobStatus" data-sentry-source-file="PortfolioExportJobInformation.tsx" />
            </div>
            <span>{schema.name}</span>
            {availablePortfolioTypes.length > 1 && schema.regionType && <Tag style={portfolioTagStyle(schema.regionType, colorMode)} className="m-0" bordered>
                {schema.regionType === "hk" ? "HKSAR" : "Global"}
              </Tag>}
            <PortfolioExportJobStatus schema={schema} canRunAutoAdaptation={canRunAutoAdaptation} isAdaptationProcessing={isAdaptationProcessing} data-sentry-element="PortfolioExportJobStatus" data-sentry-source-file="PortfolioExportJobInformation.tsx" />
          </div>
          <PortfolioExportJobMetadata schema={schema} setShowError={setShowError} canRunAutoAdaptation={canRunAutoAdaptation} data-sentry-element="PortfolioExportJobMetadata" data-sentry-source-file="PortfolioExportJobInformation.tsx" />
        </Col>
        <Col data-sentry-element="Col" data-sentry-source-file="PortfolioExportJobInformation.tsx">
          <PortfolioExportJobActions onDeleteJob={onDelete} onEditJob={onEdit} schema={schema} canRunAutoAdaptations={canRunAutoAdaptation} data-sentry-element="PortfolioExportJobActions" data-sentry-source-file="PortfolioExportJobInformation.tsx" />
        </Col>
      </Row>

      {kp.org && <PortfolioExportJobErrorModal open={showError} schema={schema} orgKey={kp.org} onCancel={() => setShowError(false)} />}
    </Col>;
};
type PortfolioExportJobMetadataType = {
  schema: PortfolioExportJobSchema;
  setShowError: Dispatch<SetStateAction<boolean>>;
  canRunAutoAdaptation: boolean;
};
const PortfolioExportJobMetadata = ({
  schema,
  setShowError,
  canRunAutoAdaptation
}: PortfolioExportJobMetadataType) => {
  const startTimeText = schema.startTime > 0 ? `${formatRelativeDate(new Date(schema.startTime))}` : "Unavailable";
  const daysAgo = schema.startTime > 0 ? `${getDaysAgo(new Date(schema.startTime))}` : "";
  const uploadCount = useSelector((state: StoreState) => state.portfolioExport.newRun.totalInBatch)[schema.id] || 0;
  const analysisComplete = getAnalysisComplete(schema, canRunAutoAdaptation);
  const showAssetsComplete = getAssetsDone(schema) && !analysisComplete;
  const showAdaptationsComplete = getHasAdaptationPhase(schema, canRunAutoAdaptation) && getAdaptationsDone(schema, canRunAutoAdaptation) && !analysisComplete;
  const ProcessingCount = () => {
    if (schema.status !== "processing") return <></>;
    const remaining = schema.assets.totalCount - schema.assets.processedCount - schema.assets.errorCount;
    if (remaining <= 0) return <></>;
    return <span data-sentry-component="ProcessingCount" data-sentry-source-file="PortfolioExportJobInformation.tsx">{remaining} processing</span>;
  };
  const AdaptationsProcessingCount = () => {
    const {
      t
    } = useTranslation("terms");
    if (!canRunAutoAdaptation) return <></>;
    const adaptations = schema.adaptations;
    if (!adaptations || adaptations.totalCount === 0) return <></>;
    const remaining = adaptations.totalCount - adaptations.processedCount - adaptations.errorCount;
    if (remaining > 0) {
      return <span className="lowercase">
          {remaining} {t("automatedResilience")} runs processing
        </span>;
    }
    if (showAdaptationsComplete) {
      return <span className="lowercase">
          <ChecksIcon size={14} weight="bold" className="mr-1" />
          {withCommas(adaptations.processedCount)} {t("automatedResilience")} runs
        </span>;
    }
    return <></>;
  };
  const UploadedCount = () => {
    if (schema.status !== "uploading") return <></>;
    return <span data-sentry-component="UploadedCount" data-sentry-source-file="PortfolioExportJobInformation.tsx">{schema.assets.totalCount - uploadCount} uploaded</span>;
  };
  const ErrorCount = () => {
    if (!schema.assets.errorCount || schema.status === "processing") return <></>;
    return <span className="pl-1" data-sentry-component="ErrorCount" data-sentry-source-file="PortfolioExportJobInformation.tsx">
        (
        <Button type="link" className="h-auto !p-0 font-[inherit] leading-none text-inherit underline underline-offset-2 hover:!text-inherit hover:no-underline" style={{
        fontWeight: "inherit",
        fontSize: "inherit"
      }} onClick={() => setShowError(true)} data-sentry-element="Button" data-sentry-source-file="PortfolioExportJobInformation.tsx">
          {withCommas(schema.assets.errorCount)} failed
        </Button>
        )
      </span>;
  };
  return <div className={classnames(styles.smallText, "mt-0.5 flex items-center text-xs leading-none text-gray-500")} data-sentry-component="PortfolioExportJobMetadata" data-sentry-source-file="PortfolioExportJobInformation.tsx">
      <span>
        <Tooltip placement="top" title={startTimeText} data-sentry-element="Tooltip" data-sentry-source-file="PortfolioExportJobInformation.tsx">
          <span>{daysAgo}</span>
        </Tooltip>
      </span>

      <span>
        <div className="flex items-center space-x-1">
          <Avatar name={schema.email} size={14} data-sentry-element="Avatar" data-sentry-source-file="PortfolioExportJobInformation.tsx" />
          <span className="ml-1">{schema.email}</span>
        </div>
      </span>

      {schema.isPrivate ? <span>
          <Tag icon={<LockFilled />} className="m-0 text-gray-500">
            Private
          </Tag>
        </span> : <span>{schema.groupName}</span>}

      <span>
        {showAssetsComplete && <ChecksIcon size={14} weight="bold" className="mr-1" />}
        {withCommas(schema.assets.totalCount)} {schema.isLargeSite ? "points" : "assets"}
        <ErrorCount data-sentry-element="ErrorCount" data-sentry-source-file="PortfolioExportJobInformation.tsx" />
      </span>
      <ProcessingCount data-sentry-element="ProcessingCount" data-sentry-source-file="PortfolioExportJobInformation.tsx" />
      {canRunAutoAdaptation && <AdaptationsProcessingCount />}
      <UploadedCount data-sentry-element="UploadedCount" data-sentry-source-file="PortfolioExportJobInformation.tsx" />
      <PortfolioExportProgressMessages schema={schema} canRunAutoAdaptation={canRunAutoAdaptation} data-sentry-element="PortfolioExportProgressMessages" data-sentry-source-file="PortfolioExportJobInformation.tsx" />
    </div>;
};